/**
 * Tipos relacionados a tarefas da equipe multiprofissional.
 */

// Enums
export const TaskStatus = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;

// eslint-disable-next-line no-redeclare
export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus];

export const TaskStatusLabels: Record<TaskStatus, string> = {
  PENDING: 'Pendente',
  IN_PROGRESS: 'Em andamento',
  COMPLETED: 'Concluída',
  CANCELLED: 'Cancelada',
};

export const TaskType = {
  FOLLOW_UP: 'FOLLOW_UP',
  PHONE_CALL: 'PHONE_CALL',
  HOME_VISIT: 'HOME_VISIT',
  EXAM_REVIEW: 'EXAM_REVIEW',
  PRESCRIPTION: 'PRESCRIPTION',
  REFERRAL: 'REFERRAL',
  QUESTIONNAIRE: 'QUESTIONNAIRE',
  CARE_PLAN: 'CARE_PLAN',
  DOCUMENTATION: 'DOCUMENTATION',
  OTHER: 'OTHER',
} as const;

// eslint-disable-next-line no-redeclare
export type TaskType = (typeof TaskType)[keyof typeof TaskType];

export const TaskTypeLabels: Record<TaskType, string> = {
  FOLLOW_UP: 'Acompanhamento',
  PHONE_CALL: 'Ligação',
  HOME_VISIT: 'Visita domiciliar',
  EXAM_REVIEW: 'Revisão de exames',
  PRESCRIPTION: 'Prescrição',
  REFERRAL: 'Encaminhamento',
  QUESTIONNAIRE: 'Questionário',
  CARE_PLAN: 'Plano de cuidado',
  DOCUMENTATION: 'Documentação',
  OTHER: 'Outro',
};

export const TaskPriority = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  URGENT: 'URGENT',
} as const;

// eslint-disable-next-line no-redeclare
export type TaskPriority = (typeof TaskPriority)[keyof typeof TaskPriority];

export const TaskPriorityLabels: Record<TaskPriority, string> = {
  LOW: 'Baixa',
  MEDIUM: 'Média',
  HIGH: 'Alta',
  URGENT: 'Urgente',
};

export const TaskPriorityColors: Record<TaskPriority, string> = {
  LOW: '#22c55e',
  MEDIUM: '#eab308',
  HIGH: '#f97316',
  URGENT: '#ef4444',
};

// Recurrence patterns
export const RecurrencePattern = {
  DAILY: 'DAILY',
  WEEKLY: 'WEEKLY',
  BIWEEKLY: 'BIWEEKLY',
  MONTHLY: 'MONTHLY',
  QUARTERLY: 'QUARTERLY',
  YEARLY: 'YEARLY',
} as const;

// eslint-disable-next-line no-redeclare
export type RecurrencePattern = (typeof RecurrencePattern)[keyof typeof RecurrencePattern];

export const RecurrencePatternLabels: Record<RecurrencePattern, string> = {
  DAILY: 'Diário',
  WEEKLY: 'Semanal',
  BIWEEKLY: 'Quinzenal',
  MONTHLY: 'Mensal',
  QUARTERLY: 'Trimestral',
  YEARLY: 'Anual',
};

export const DayOfWeekLabels: Record<number, string> = {
  1: 'Segunda-feira',
  2: 'Terça-feira',
  3: 'Quarta-feira',
  4: 'Quinta-feira',
  5: 'Sexta-feira',
  6: 'Sábado',
  7: 'Domingo',
};

// Interfaces
export interface TaskListItem {
  id: string;
  title: string;
  type: TaskType;
  typeLabel: string;
  priority: TaskPriority;
  priorityLabel: string;
  priorityColor: string;
  status: TaskStatus;
  statusLabel: string;
  dueDate: string | null;
  overdue: boolean;
  patientId: string | null;
  patientName: string | null;
  assignedToId: string | null;
  assignedToName: string | null;
  createdById: string;
  createdByName: string;
  createdAt: string;
}

export interface TaskDetail extends TaskListItem {
  description: string | null;
  patientCpf: string | null;
  assignedToEmail: string | null;
  completedAt: string | null;
  completedById: string | null;
  completedByName: string | null;
  completionNotes: string | null;
  updatedAt: string;
  // Recurrence fields
  isRecurring: boolean;
  recurrencePattern: RecurrencePattern | null;
  recurrencePatternLabel: string | null;
  recurrenceInterval: number | null;
  recurrenceDayOfWeek: number | null;
  recurrenceDayOfMonth: number | null;
  recurrenceEndDate: string | null;
  recurrenceCount: number | null;
  parentTaskId: string | null;
  occurrenceNumber: number | null;
  // Related entities
  relatedDocumentId: string | null;
  relatedMessageId: string | null;
}

export interface TaskStats {
  totalPending: number;
  totalInProgress: number;
  totalCompleted: number;
  totalOverdue: number;
  completedToday: number;
  completedThisWeek: number;
  byType: Record<string, number>;
  byPriority: Record<string, number>;
}

export interface TaskFilters {
  search?: string;
  statuses?: TaskStatus[];
  types?: TaskType[];
  priorities?: TaskPriority[];
  assignedToId?: string;
  patientId?: string;
  createdById?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  overdueOnly?: boolean;
  unassignedOnly?: boolean;
}

export interface CreateTaskRequest {
  patientId?: string;
  title: string;
  description?: string;
  type: TaskType;
  priority: TaskPriority;
  dueDate?: string;
  assignedToId?: string;
  // Recurrence options
  isRecurring?: boolean;
  recurrencePattern?: RecurrencePattern;
  recurrenceInterval?: number;
  recurrenceDayOfWeek?: number;
  recurrenceDayOfMonth?: number;
  recurrenceEndDate?: string;
  recurrenceCount?: number;
}

export interface UpdateTaskRequest {
  title?: string;
  description?: string;
  type?: TaskType;
  priority?: TaskPriority;
  dueDate?: string;
  assignedToId?: string;
}

export interface CompleteTaskRequest {
  notes?: string;
}
